<template>
    <div class="quickpay-payment-ticket">
        <p class="quickpay-payment-ticket__company-name typographies-paragraph-x-large-semibold">
            {{ data.corporation.name }}
        </p>
        <p class="quickpay-payment-ticket__date typographies-paragraph-medium-regular">
            {{ formattedCurrentDate }}
        </p>
        <div class="quickpay-payment-ticket__header">
            <div class="quickpay-payment-ticket__header-content">
                <p
                    v-if="pdfType"
                    class="quickpay-payment-ticket__type typographies-paragraph-large-semibold"
                >
                    {{ pdfType }}
                </p>
                <p
                    v-if="data.document.number"
                    class="quickpay-payment-ticket__title typographies-paragraph-medium-regular"
                >
                    N° {{ data.document.number }}
                </p>
            </div>
            <TagStatus :id="pdfStatus.id" :label="pdfStatus.label" size="small" />
        </div>
        <div class="quickpay-payment-ticket__transaction-details-section">
            <p
                class="quickpay-payment-ticket__transaction-details typographies-paragraph-large-semibold"
            >
                {{ translations.transactionDetails }}
            </p>
            <div class="quickpay-payment-ticket__rows">
                <div
                    v-for="row in transactionRows"
                    :key="row.key"
                    class="quickpay-payment-ticket__row"
                >
                    <span
                        class="quickpay-payment-ticket__label typographies-paragraph-medium-regular"
                    >
                        {{ row.label }}
                    </span>
                    <span
                        class="quickpay-payment-ticket__value typographies-paragraph-medium-regular"
                    >
                        {{ row.value }}
                    </span>
                </div>
            </div>
        </div>
        <CompassButton
            :label="$t.__('common.actions.back_to_document')"
            type="tertiary"
            class="quickpay-payment-ticket__btn"
            @click="$emit('back')"
        />
    </div>
</template>

<script>
import TagStatus from 'vcomponents/common/TagStatus/TagStatus.vue';
import SalesDocumentStatus from 'Config/authenticated/SalesDocumentsStatus';
import docTypeLabel from 'Dictionaries/docTypeLabel';
import moment from 'moment';

export default {
    components: {
        TagStatus,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            translations: {
                transactionDetails: this.$t.__('common.labels.transaction_details'),
                company: this.$t.__('common.labels.company'),
                date: this.$t.__('common.labels.date'),
                provider: this.$t.__('common.labels.supplier'),
                paymentMethod: this.$t.__('common.labels.payment_method'),
                total: this.$t.__('common.labels.total'),
                bankCard: this.$t.__('common.labels.bank_card'),
            },
        };
    },
    computed: {
        userLocale() {
            return navigator.language || 'en-US';
        },
        pdfType() {
            return docTypeLabel[this.data.document.doctype]?.label || '';
        },
        pdfStatus() {
            if (this.data.payment_success?.processing) {
                return {
                    id: 'processing',
                    label: this.$t.__('quickpay.QuickpayPaymentTicket.payment_processing'),
                };
            }
            return {
                id: this.data.document.step,
                label: SalesDocumentStatus[this.data.document.doctype][this.data.document.step]
                    .shortLabel,
            };
        },
        paymentSuccess() {
            return (
                this.data.payment_success || {
                    method: 'stripe',
                    processing: false,
                    type: 'card',
                    amount: 55.56,
                }
            );
        },
        formattedAmount() {
            const amount = this.paymentSuccess.amount;
            if (amount === undefined || amount === null) {
                return '';
            }
            const currency = this.data.document?.currency || 'EUR';
            return new Intl.NumberFormat(this.userLocale, {
                style: 'currency',
                currency: currency,
            }).format(amount);
        },
        formattedCurrentDate() {
            moment.locale(this.userLocale);
            return moment().format('L LT');
        },
        paymentMethodDisplay() {
            const type = this.paymentSuccess.type;
            const method = this.paymentSuccess.method;
            if (type === 'directdebit') {
                return 'SEPA';
            }
            if (method === 'paypal') {
                return 'PayPal';
            }
            return this.translations.bankCard;
        },
        transactionRows() {
            const rows = [
                {
                    key: 'provider',
                    label: this.translations.provider,
                    value:
                        this.paymentSuccess.method.charAt(0).toUpperCase() +
                        this.paymentSuccess.method.slice(1),
                },
                {
                    key: 'payment_method',
                    label: this.translations.paymentMethod,
                    value: this.paymentMethodDisplay,
                },
                {
                    key: 'total',
                    label: this.translations.total,
                    value: this.formattedAmount,
                },
            ];
            return rows;
        },
    },
};
</script>

<style lang="scss" scoped>
@keyframes ticket-print {
    from {
        transform: translateY(100%) translateZ(0);
        opacity: 0;
    }
    to {
        transform: translateY(0) translateZ(0);
        opacity: 1;
    }
}

.quickpay-payment-ticket {
    position: relative;
    background: var(--colors-common-background-default);
    width: 400px;
    mask: url('../ressources/logged/img/illustrations/payment/ticket-wave-mask.svg') no-repeat
        center / 100% 100%;
    -webkit-mask: url('../ressources/logged/img/illustrations/payment/ticket-wave-mask.svg') no-repeat
        center / 100% 100%;
    padding: var(--spacing-5);
    margin: 0 0 var(--spacing-3);
    animation: ticket-print 1s ease-in-out;

    // Force GPU acceleration to prevent mask rendering glitches
    transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    will-change: transform;

    @media (max-width: 1023px) {
        width: 100%;
        max-width: 448px;
    }

    @media (max-width: 500px) {
        padding: var(--spacing-4);
    }

    &__company-name {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        color: var(--colors-common-text-standard-default);
        margin: var(--spacing-3) 0 0;
    }

    &__date {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        color: var(--colors-common-text-standard-default);
        margin: var(--spacing-1) 0 0;
        padding-bottom: var(--spacing-5);
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: repeating-linear-gradient(
                to right,
                var(--colors-common-border-default) 0,
                var(--colors-common-border-default) 7px,
                transparent 7px,
                transparent 14px
            );
        }
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: var(--spacing-3) 0;
        padding-bottom: var(--spacing-3);
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: repeating-linear-gradient(
                to right,
                var(--colors-common-border-default) 0,
                var(--colors-common-border-default) 7px,
                transparent 7px,
                transparent 14px
            );
        }
    }
    &__header-content {
        display: flex;
        flex-direction: column;
    }
    &__type,
    &__title {
        color: var(--colors-common-text-standard-default);
        margin: 0;
    }

    &__transaction-details-section {
        margin: var(--spacing-3) 0;
    }
    &__transaction-details {
        margin: 0 0 var(--spacing-1);
        color: var(--colors-common-text-standard-default);
    }
    &__rows {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }
    &__row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }
    &__label,
    &__value {
        color: var(--colors-common-text-standard-default);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    &__label {
        text-align: left;
        flex: 1 1 0;
        min-width: 0;
    }
    &__value {
        text-align: right;
        flex: 1 1 0;
        min-width: 0;
    }

    &__btn {
        width: 100%;
    }
}
</style>
